import { User } from '../auth/User';
import { IUserInvite, SystemUserInvite, UserInvite } from '../types/UserInvite';
import { ApiRoutes, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';
import { ApiError, ApiErrorDetails } from './ApiError';

/**
 * Invite item for a single company (v2 API)
 */
export interface InviteCompanyItem {
  companyId: string;
  adminLevel?: string;
  locations: string[];
  customRoleIds?: string[];
}

/**
 * Request body for inviting a user to companies (v2 API)
 */
export interface InviteUserRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email: string;
  invite: InviteCompanyItem[];
}

/**
 * Legacy API class for user invitation operations (v1)
 * Used by existing providers (members.ts, users.ts)
 */
export class UserInviteAPI {
  //Add a user to your company
  static async add(user: User, data: UserInvite): Promise<{} | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.INVITE_USER),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async addSystemUser(user: User, data: SystemUserInvite): Promise<any> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.INVITE_SYSTEMS_USER),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result?.data;
  }

  /**
   * Invites an internal system user to the platform using the V2 API endpoint.
   * This method sends an invitation to a new system user.
   *
   * @param user - The authenticated user making the request
   * @param data - The invitation data containing user details and admin level
   * @returns Promise resolving to the API response data containing the invited user details
   */
  static async addInternalUser(
    user: User,
    data: IUserInvite,
  ): Promise<{ success: boolean; data?: unknown; error?: string; errorDetails?: ApiErrorDetails }> {
    try {
      const result = await HttpRequest(
        getFullApiRoute(ApiRoutes.INVITE_SYSTEMS_USER_V3),
        'POST',
        data,
        new Headers({ 'Content-Type': 'application/json' }),
        user,
      );
      return { success: true, data: result?.data };
    } catch (error) {
      // Rethrow ApiError for auth-related errors (401/403) so session refresh logic can handle them
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        throw error;
      }

      // For other errors, return structured error response
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to invite internal user',
        errorDetails: error instanceof ApiError ? error.details : null,
      };
    }
  }

  //Remove a user from your company
  static async remove(
    user: User,
    data: { userId: string; companyId: string },
  ): Promise<{} | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.UNINVITE_USER),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async resendInvitation(
    user: User,
    data: { userId: string; companyId?: string },
  ): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      const result = await HttpRequest(
        replaceRouteParams(getFullApiRoute(ApiRoutes.RESEND_INVITATION_V3), {
          userId: data.userId,
        }),
        'POST',
        { companyUuid: data.companyId },
        new Headers({ 'Content-Type': 'application/json' }),
        user,
      );
      return { success: true, data: result };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resend invitation',
      };
    }
  }

  // static async remove(user: User, data: { userId: string; companyId: string }): Promise<unknown> {
  //   const result = await HttpRequest(
  //     getFullApiRoute(ApiRoutes.UNINVITE_USER),
  //     'POST',
  //     data,
  //     new Headers({ 'Content-Type': 'application/json' }),
  //     user,
  //   );
  //   return result;
  // }

  static async inviteUser(
    user: User,
    data: InviteUserRequest,
  ): Promise<{ success: boolean; data?: unknown; error?: string; errorDetails?: ApiErrorDetails }> {
    try {
      const result = await HttpRequest(
        getFullApiRoute(ApiRoutes.INVITE_USER_V3),
        'POST',
        data,
        new Headers({ 'Content-Type': 'application/json' }),
        user,
      );
      return { success: true, data: result };
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        throw error;
      }

      // For errors, return structured error response
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to invite user',
        errorDetails: error instanceof ApiError ? error.details : null,
      };
    }
  }
}
